/**
 * Filters product gallery media when "Hide other variants' media" is enabled.
 * Stacked layout: show only the selected variant's featured image.
 * Other layouts: filter by alt text color; active slide is always the variant image.
 */
(function () {
  const COLOR_OPTION_PATTERN = /^(color|colour)$/i;
  const COLOR_PART_SPLIT_PATTERN = /\s*(?:\/|&|,|\band\b)\s*/i;

  function normalizeColorLabel(colorLabel) {
    if (!colorLabel) return '';
    return colorLabel
      .replace(/\s*[-–—]{1,2}\s*#?[0-9a-f]{3,8}\s*$/i, '')
      .split('--')[0]
      .trim();
  }

  function escapeRegex(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function compactColorString(value) {
    return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function splitColorParts(colorLabel) {
    const normalizedLabel = normalizeColorLabel(colorLabel);
    if (!normalizedLabel) return [];

    return normalizedLabel
      .split(COLOR_PART_SPLIT_PATTERN)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  function hasDelimitedSegments(text) {
    return /[-_]/.test(text || '');
  }

  function getAltTextSegments(text) {
    return (text || '')
      .split(/[-_]+/)
      .map((segment) => segment.replace(/\.[a-z0-9]+$/i, ''))
      .map(compactColorString)
      .filter(Boolean);
  }

  function buildColorMatchers(colorLabel) {
    const parts = splitColorParts(colorLabel).map(compactColorString).filter(Boolean);
    if (!parts.length) return [];

    if (parts.length === 1) {
      return [new RegExp(escapeRegex(parts[0]), 'i')];
    }

    const combined = parts.join('');
    return [
      new RegExp(escapeRegex(combined), 'i'),
      new RegExp(parts.map(escapeRegex).join('(?:and)?'), 'i'),
      new RegExp(parts.map(escapeRegex).join('(?:\\s+and\\s+|[\\W_]*)'), 'i'),
    ];
  }

  function multiPartColorMatchesSpacedText(searchText, compactParts) {
    const tokens = (searchText || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length < compactParts.length) return false;

    for (let start = 0; start <= tokens.length - compactParts.length; start++) {
      let tokenIndex = start;
      let matched = true;

      for (let partIndex = 0; partIndex < compactParts.length; partIndex++) {
        if (compactColorString(tokens[tokenIndex]) !== compactParts[partIndex]) {
          matched = false;
          break;
        }

        tokenIndex += 1;

        if (partIndex < compactParts.length - 1) {
          const joiner = tokens[tokenIndex];
          if (joiner && /^(?:and|&|\/)?$/i.test(joiner)) {
            tokenIndex += 1;
          }
        }
      }

      if (matched) return true;
    }

    return false;
  }

  function singlePartColorMatchesDelimitedText(searchText, compactColorPart) {
    const tokens = (searchText || '').split(/\s+/).filter(Boolean);
    let checkedDelimited = false;

    for (const token of tokens) {
      if (!hasDelimitedSegments(token)) continue;

      checkedDelimited = true;
      const segments = getAltTextSegments(token);
      if (segments.includes(compactColorPart)) return true;
    }

    return checkedDelimited ? false : null;
  }

  function getMediaSearchableText(mediaItem) {
    return mediaItem.dataset.mediaTitle || '';
  }

  function textMatchesColor(searchText, colorLabel) {
    const parts = splitColorParts(colorLabel).map(compactColorString).filter(Boolean);
    if (!parts.length) return false;

    if (parts.length === 1) {
      const delimitedMatch = singlePartColorMatchesDelimitedText(searchText, parts[0]);
      if (delimitedMatch !== null) return delimitedMatch;
    }

    if (parts.length > 1 && multiPartColorMatchesSpacedText(searchText, parts)) {
      return true;
    }

    const searchableValues = [searchText, compactColorString(searchText)];
    const matchers = buildColorMatchers(colorLabel);

    return matchers.some((matcher) => searchableValues.some((value) => matcher.test(value)));
  }

  function isSubstringOfMatchingColor(searchText, selectedColor, allColors) {
    const selectedParts = splitColorParts(selectedColor).map(compactColorString).filter(Boolean);
    if (selectedParts.length !== 1) return false;

    const selectedCompact = selectedParts[0];

    return allColors.some((otherColor) => {
      if (normalizeColorLabel(otherColor) === normalizeColorLabel(selectedColor)) return false;

      const otherParts = splitColorParts(otherColor).map(compactColorString).filter(Boolean);
      const otherCompact = otherParts.length > 1 ? otherParts.join('') : otherParts[0];
      if (!otherCompact || otherCompact.length <= selectedCompact.length) return false;
      if (!otherCompact.includes(selectedCompact)) return false;

      return textMatchesColor(searchText, otherColor);
    });
  }

  function textMatchesAnyColor(searchText, colorLabels) {
    return colorLabels.some((colorLabel) => textMatchesColor(searchText, colorLabel));
  }

  function shouldShowMedia(searchText, selectedColor, allColors) {
    if (!textMatchesAnyColor(searchText, allColors)) return null;

    if (!textMatchesColor(searchText, selectedColor)) return false;

    if (isSubstringOfMatchingColor(searchText, selectedColor, allColors)) return false;

    return true;
  }

  function isVideoGalleryItem(item) {
    return item.dataset.galleryVideo === 'true' || Boolean(
      item.querySelector('.thumbnail__badge .icon-play, .product__media-item--full deferred-media, video')
    );
  }

  function resolveItemVisibility(item, selectedColor, allColors) {
    const searchText = getMediaSearchableText(item);
    const colorMatch = shouldShowMedia(searchText, selectedColor, allColors);

    if (colorMatch !== null) {
      return colorMatch;
    }

    return true;
  }

  function readAllColors(variantSelects, mediaGallery) {
    const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
    for (const fieldset of fieldsets) {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const firstInput = inputs[0];
      if (!firstInput || !isColorOptionName(getControlOptionName(firstInput))) continue;

      return inputs
        .map((input) => normalizeColorLabel(input.value || ''))
        .filter(Boolean);
    }

    const selects = Array.from(variantSelects.querySelectorAll('select'));
    for (const select of selects) {
      if (!isColorOptionName(getControlOptionName(select))) continue;

      return Array.from(select.options)
        .map((option) => normalizeColorLabel(option.value || ''))
        .filter(Boolean);
    }

    return (mediaGallery.dataset.colorOptions || '')
      .split('||')
      .map((color) => normalizeColorLabel(color))
      .filter(Boolean);
  }

  function isColorOptionName(optionName) {
    return COLOR_OPTION_PATTERN.test((optionName || '').trim());
  }

  function getControlOptionName(control) {
    const name = control?.getAttribute('name') || '';
    const optionMatch = name.match(/^options\[(.*)\]$/);
    return optionMatch ? optionMatch[1] : name;
  }

  function readSelectedColor(variantSelects) {
    const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
    for (const fieldset of fieldsets) {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const firstInput = inputs[0];
      if (!firstInput || !isColorOptionName(getControlOptionName(firstInput))) continue;

      const checkedInput = inputs.find((input) => input.checked);
      if (checkedInput?.value) return normalizeColorLabel(checkedInput.value);
    }

    const selects = Array.from(variantSelects.querySelectorAll('select'));
    for (const select of selects) {
      if (!isColorOptionName(getControlOptionName(select))) continue;
      if (select.value) return normalizeColorLabel(select.value);
    }

    const colorOptionIndex = variantSelects.getProductColorOptionIndex?.();
    if (colorOptionIndex >= 0 && typeof variantSelects.getOptionsForVariantMatch === 'function') {
      const options = variantSelects.getOptionsForVariantMatch();
      const color = options?.[colorOptionIndex];
      if (color) return normalizeColorLabel(color);
    }

    return null;
  }

  function getMediaImageKey(mediaItem) {
    const img = mediaItem.querySelector('img');
    if (!img) return '';

    const src = img.currentSrc || img.src || '';
    if (!src) return '';

    try {
      const { pathname } = new URL(src, window.location.origin);
      return decodeURIComponent(pathname).toLowerCase();
    } catch (error) {
      return src.split('?')[0].toLowerCase();
    }
  }

  function getViewerSlides(mediaGallery, sectionId) {
    const viewer = mediaGallery.querySelector(`#GalleryViewer-${sectionId}`);
    if (!viewer) return [];

    return Array.from(viewer.querySelectorAll('.product__media-item[data-media-id]'));
  }

  function syncThumbnailVisibility(mediaGallery, sectionId) {
    const thumbnails = mediaGallery.querySelector(`#GalleryThumbnails-${sectionId}`);
    if (!thumbnails) return;

    thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
      const slide = mediaGallery.querySelector(
        `#GalleryViewer-${sectionId} [data-media-id="${thumbnail.dataset.target}"]`
      );
      if (!slide) return;

      const shouldHide = slide.hidden || slide.classList.contains('is-variant-color-filtered-out');
      setGalleryItemVisibility(thumbnail, shouldHide);
    });
  }

  function getGalleryItems(mediaGallery, sectionId) {
    const viewer = mediaGallery.querySelector(`#GalleryViewer-${sectionId}`);
    const thumbnails = mediaGallery.querySelector(`#GalleryThumbnails-${sectionId}`);
    const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
    const items = new Set();

    viewer?.querySelectorAll('[data-product-gallery-media]').forEach((item) => items.add(item));
    thumbnails?.querySelectorAll('[data-product-gallery-media]').forEach((item) => items.add(item));
    modalContent?.querySelectorAll('[data-media-id]').forEach((item) => items.add(item));

    return Array.from(items);
  }

  function setGalleryItemVisibility(item, shouldHide) {
    item.hidden = shouldHide;
    item.style.display = shouldHide ? 'none' : '';
    item.classList.toggle('is-variant-color-filtered-out', shouldHide);

    if (!shouldHide) return;

    item.classList.remove('is-active');
    item.querySelectorAll('deferred-media').forEach((media) => media.resetContent?.());
    const thumbnailButton = item.querySelector('button[aria-current]');
    if (thumbnailButton) thumbnailButton.removeAttribute('aria-current');
  }

  function getFirstVisibleMediaId(mediaGallery) {
    const visibleItem = mediaGallery.elements?.viewer?.querySelector(
      '[data-media-id]:not([hidden]):not(.is-variant-color-filtered-out)'
    );
    return visibleItem?.dataset.mediaId || null;
  }

  function resolveMediaVariant(picker) {
    return (
      picker.currentVariant ||
      picker.getMediaVariant?.({ allowWithoutHideVariants: true }) ||
      picker.getFallbackMediaVariantForSelectedColor?.() ||
      null
    );
  }

  function resolveSelectedMediaId(picker, sectionId) {
    const mediaVariant = resolveMediaVariant(picker);
    if (!mediaVariant) return null;

    const featuredMediaId = picker.getVariantFeaturedMediaId?.(mediaVariant);
    if (!featuredMediaId) return null;

    return `${sectionId}-${featuredMediaId}`;
  }

  function getRawMediaIdFromGalleryId(galleryMediaId) {
    const match = String(galleryMediaId).match(/-(\d+)$/);
    return match ? match[1] : String(galleryMediaId);
  }

  function applySingleVariant(mediaGallery, sectionId, selectedMediaId) {
    if (!selectedMediaId) {
      reset(mediaGallery, sectionId);
      return;
    }

    const rawMediaId = getRawMediaIdFromGalleryId(selectedMediaId);

    getViewerSlides(mediaGallery, sectionId).forEach((item) => {
      setGalleryItemVisibility(item, item.dataset.mediaId !== selectedMediaId);
    });

    syncThumbnailVisibility(mediaGallery, sectionId);

    const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
    modalContent?.querySelectorAll('[data-media-id]').forEach((item) => {
      setGalleryItemVisibility(item, String(item.dataset.mediaId) !== rawMediaId);
    });

    mediaGallery.classList.add('media-gallery--color-filter-active');
    delete mediaGallery.dataset.activeGalleryColor;

    if (typeof mediaGallery.setActiveMedia === 'function') {
      mediaGallery.setActiveMedia(selectedMediaId, true);
      mediaGallery.refreshSwiperInstances?.();
      return;
    }

    mediaGallery.elements?.viewer?.resetPages?.();
    mediaGallery.elements?.thumbnails?.resetPages?.();
    mediaGallery.syncThumbnailArrowVisibility?.();
    mediaGallery.refreshSwiperInstances?.();
  }

  function reset(mediaGallery, sectionId) {
    getGalleryItems(mediaGallery, sectionId).forEach((item) => setGalleryItemVisibility(item, false));
    mediaGallery.classList.remove('media-gallery--color-filter-active');
    delete mediaGallery.dataset.activeGalleryColor;
    mediaGallery.elements?.viewer?.resetPages?.();
    mediaGallery.elements?.thumbnails?.resetPages?.();
    mediaGallery.syncThumbnailArrowVisibility?.();
    mediaGallery.refreshSwiperInstances?.();
  }

  function isSlideVisible(slide) {
    return slide && !slide.hidden && !slide.classList.contains('is-variant-color-filtered-out');
  }

  function apply(mediaGallery, sectionId, { selectedColor, allColors, selectedMediaId }) {
    const seenImageKeys = new Set();
    const viewerSlides = getViewerSlides(mediaGallery, sectionId);
    let firstVisibleMediaId = null;

    viewerSlides.forEach((item) => {
      let shouldShow = resolveItemVisibility(item, selectedColor, allColors);

      if (shouldShow) {
        const imageKey = getMediaImageKey(item);
        if (imageKey) {
          if (seenImageKeys.has(imageKey)) {
            shouldShow = false;
          } else {
            seenImageKeys.add(imageKey);
          }
        }
      }

      setGalleryItemVisibility(item, !shouldShow);

      if (shouldShow && !firstVisibleMediaId && item.dataset.mediaId) {
        firstVisibleMediaId = item.dataset.mediaId;
      }
    });

    syncThumbnailVisibility(mediaGallery, sectionId);

    const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
    modalContent?.querySelectorAll('[data-media-id]').forEach((item) => {
      const shouldShow = resolveItemVisibility(item, selectedColor, allColors);
      setGalleryItemVisibility(item, !shouldShow);
    });

    mediaGallery.classList.add('media-gallery--color-filter-active');
    mediaGallery.dataset.activeGalleryColor = selectedColor;

    const variantSlide = selectedMediaId
      ? viewerSlides.find((item) => item.dataset.mediaId === selectedMediaId)
      : null;
    const useVariantImage = isSlideVisible(variantSlide);
    const nextMediaId = useVariantImage
      ? selectedMediaId
      : firstVisibleMediaId || getFirstVisibleMediaId(mediaGallery);

    if (nextMediaId && typeof mediaGallery.setActiveMedia === 'function') {
      mediaGallery.setActiveMedia(nextMediaId, useVariantImage);
    }

    mediaGallery.refreshSwiperInstances?.();

    mediaGallery.elements?.viewer?.resetPages?.();
    mediaGallery.elements?.thumbnails?.resetPages?.();
    mediaGallery.syncThumbnailArrowVisibility?.();
  }

  function getPrimaryVariantPicker(sectionId) {
    const pickers = document.querySelectorAll(
      `variant-selects[data-section="${sectionId}"], variant-radios[data-section="${sectionId}"]`
    );

    return (
      Array.from(pickers).find(
        (picker) => !picker.parentElement?.closest('variant-selects, variant-radios')
      ) || null
    );
  }

  function syncFromVariantSelects(variantSelects, mediaGallery) {
    if (!variantSelects || !mediaGallery) return;

    const sectionId = variantSelects.dataset.section;
    const primaryPicker = getPrimaryVariantPicker(sectionId);

    if (primaryPicker && primaryPicker !== variantSelects) return;

    const picker = primaryPicker || variantSelects;

    if (mediaGallery.dataset.desktopLayout === 'stacked') {
      const selectedMediaId = resolveSelectedMediaId(picker, sectionId);
      if (!selectedMediaId) {
        reset(mediaGallery, sectionId);
        return;
      }

      applySingleVariant(mediaGallery, sectionId, selectedMediaId);
      return;
    }

    if (typeof picker.hasProductColorOption === 'function' && !picker.hasProductColorOption()) return;

    const allColors = readAllColors(picker, mediaGallery);

    if (!picker.getMediaVariant?.({ allowWithoutHideVariants: true })) {
      reset(mediaGallery, sectionId);
      return;
    }

    const selectedColor = readSelectedColor(picker);
    if (!selectedColor) {
      reset(mediaGallery, sectionId);
      return;
    }

    const selectedMediaId = resolveSelectedMediaId(picker, sectionId);
    apply(mediaGallery, sectionId, { selectedColor, allColors, selectedMediaId });
  }

  function syncAllFromVariantPickers() {
    document.querySelectorAll('media-gallery[data-hide-variants="true"]').forEach((mediaGallery) => {
      const sectionId = mediaGallery.id.replace('MediaGallery-', '');
      const primaryPicker = getPrimaryVariantPicker(sectionId);
      if (!primaryPicker) return;
      syncFromVariantSelects(primaryPicker, mediaGallery);
    });
  }

  window.VariantMediaFilter = {
    apply,
    applySingleVariant,
    reset,
    syncFromVariantSelects,
    syncAllFromVariantPickers,
    getPrimaryVariantPicker,
    // Exported for debugging and unit-style verification in the console.
    _internals: {
      buildColorMatchers,
      compactColorString,
      getAltTextSegments,
      getMediaSearchableText,
      getMediaImageKey,
      hasDelimitedSegments,
      isSubstringOfMatchingColor,
      multiPartColorMatchesSpacedText,
      resolveItemVisibility,
      isVideoGalleryItem,
      readAllColors,
      normalizeColorLabel,
      singlePartColorMatchesDelimitedText,
      splitColorParts,
      textMatchesColor,
      textMatchesAnyColor,
    },
  };
  syncAllFromVariantPickers();
  document.addEventListener('DOMContentLoaded', syncAllFromVariantPickers, { once: true });
})();
